import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CaseDocumentUploaderView,
  formatUploadLimit,
  maxUploadBytes,
  SUPPORTED_TYPES,
  type UploadedFile,
} from '@/components/documents/CaseDocumentUploaderView'
import { UploadConflictDialog } from '@/components/documents/UploadConflictDialog'
import { captureRefusal } from '@/features/documents/capture-integrity'
import {
  appendTableProcessingMode,
  isTableFilename,
  updateStagedTableMode,
} from '@/features/documents/table-processing'
import { videoLimitRefusal } from '@/features/documents/video-limits'
import { videoAnalysisRefusal } from '@/features/documents/video-refusal'
import { api, apiClient } from '@/services/api'
import type { CaseDocumentUploadResponse, TabularExtractionMode } from '@/services/api/cases'
import { findDuplicateTargetPaths } from '@/utils/upload-conflicts'
import { isVideoUploadUnavailable } from '@/utils/video-file'

interface CaseDocumentUploaderProps {
  caseId: string
  caseName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete?: () => void
  basePath?: string // current folder path — prepended to relative_path
  /** When provided, renders a small "Import from Data Lake" affordance in the footer that lets the user switch flows without leaving the page. */
  onSwitchToLake?: () => void
}

interface UploadErrorResponse {
  response?: { data?: { detail?: string } }
}

/**
 * Connected case document uploader. Owns the staged file list and the upload
 * mutation (multipart POST per file) and feeds the pure
 * {@link CaseDocumentUploaderView}.
 */
export function CaseDocumentUploader({
  caseId,
  caseName,
  open,
  onOpenChange,
  onUploadComplete,
  basePath,
  onSwitchToLake,
}: CaseDocumentUploaderProps) {
  const { t } = useTranslation()
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  // Non-empty while the same-name conflict prompt is shown (SP-357).
  const [conflicts, setConflicts] = useState<string[]>([])
  // The day the batch was received on (ADR-0094); '' leaves it to the upload time.
  const [receivedOn, setReceivedOn] = useState('')
  const [tableMode, setTableMode] = useState<TabularExtractionMode>('structured')

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const validFiles: UploadedFile[] = []
      const errors: string[] = []

      for (const file of newFiles) {
        if (isVideoUploadUnavailable(file)) {
          errors.push(`${file.name}: ${t('documents.video.temporarilyUnavailable')}`)
          continue
        }
        const limit = maxUploadBytes(file.name)
        if (file.size > limit) {
          errors.push(`${file.name}: File too large (max ${formatUploadLimit(limit)})`)
          continue
        }

        const ext = `.${file.name.split('.').pop()?.toLowerCase()}`
        if (!SUPPORTED_TYPES.includes(ext)) {
          errors.push(`${file.name}: Unsupported file type`)
          continue
        }

        const webkitPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath
        const relativePath = webkitPath || null

        validFiles.push({
          file,
          id: Math.random().toString(36).substr(2, 9),
          relativePath,
          progress: 0,
          status: 'pending',
        })
      }

      if (errors.length > 0) {
        toast.error('Some files were skipped', { description: errors.join('\n') })
      }

      setFiles(prev => [...prev, ...validFiles])
    },
    [t]
  )

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }, [])

  const clearFiles = useCallback(() => setFiles([]), [])

  const changeFileTableMode = useCallback(
    (id: string, mode: TabularExtractionMode | undefined) => {
      if (!uploading) setFiles(prev => updateStagedTableMode(prev, id, mode))
    },
    [uploading]
  )

  // Folder path a file lands in:
  // - Single file: just basePath (current folder)
  // - Folder upload: basePath + subfolders from webkitRelativePath
  //   ("UploadedFolder/sub/file.pdf" → keep "UploadedFolder/sub" so the
  //   uploaded folder appears as a child of the current location).
  const buildFolderPath = useCallback(
    (fileItem: UploadedFile): string => {
      let folderPath = basePath || ''
      if (fileItem.relativePath?.includes('/')) {
        const dirPart = fileItem.relativePath.substring(0, fileItem.relativePath.lastIndexOf('/'))
        folderPath = folderPath ? `${folderPath}/${dirPart}` : dirPart
      }
      return folderPath
    },
    [basePath]
  )

  const runUpload = useCallback(
    async (replace: boolean) => {
      setUploading(true)

      let successCount = 0
      let errorCount = 0

      for (const fileItem of files) {
        if (fileItem.status === 'uploaded') {
          successCount++
          continue
        }

        setFiles(prev =>
          prev.map(f => (f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f))
        )

        try {
          const formData = new FormData()
          formData.append('file', fileItem.file)
          appendTableProcessingMode(formData, fileItem.file.name, tableMode, fileItem.tableMode)
          const folderPath = buildFolderPath(fileItem)
          if (folderPath) {
            formData.append('relative_path', folderPath)
          }
          if (replace) {
            formData.append('replace', 'true')
          }
          if (receivedOn) {
            formData.append('received_at', receivedOn)
          }

          const response = await apiClient.post<Partial<CaseDocumentUploadResponse>>(
            `/v1/cases/${caseId}/documents/upload`,
            formData
          )

          const documentId = response?.document_id
          setFiles(prev =>
            prev.map(f =>
              f.id === fileItem.id ? { ...f, status: 'uploaded', progress: 100, documentId } : f
            )
          )

          successCount++
        } catch (error) {
          console.error(`Upload failed for ${fileItem.file.name}:`, error)
          // A same-name HUMINT capture is never replaced (ADR-0045); a video over the limits is
          // refused with the limit it broke (PRD-0074).
          const detail =
            captureRefusal(error, t) ??
            videoAnalysisRefusal(error, t) ??
            videoLimitRefusal(error, fileItem.file.name, t) ??
            (error as UploadErrorResponse)?.response?.data?.detail
          setFiles(prev =>
            prev.map(f =>
              f.id === fileItem.id ? { ...f, status: 'error', error: detail || 'Upload failed' } : f
            )
          )
          errorCount++
        }
      }

      setUploading(false)

      if (errorCount === 0) {
        toast.success('Upload complete', {
          description: `${successCount} ${successCount === 1 ? 'file' : 'files'} uploaded successfully`,
        })
      } else {
        toast.warning('Upload completed with errors', {
          description: `${successCount} succeeded, ${errorCount} failed`,
        })
      }

      if (successCount > 0 && onUploadComplete) {
        onUploadComplete()
      }

      if (successCount > 0) {
        setTimeout(() => {
          onOpenChange(false)
          setFiles([])
          setReceivedOn('')
          setTableMode('structured')
        }, 1500)
      }
    },
    [files, buildFolderPath, caseId, onUploadComplete, onOpenChange, receivedOn, tableMode, t]
  )

  const uploadAll = useCallback(async () => {
    if (files.length === 0) {
      toast.error('No files selected', { description: 'Please add files to upload' })
      return
    }

    const pending = files.filter(f => f.status !== 'uploaded')
    const targetPaths = pending.map(f => {
      const folderPath = buildFolderPath(f)
      return folderPath ? `${folderPath}/${f.file.name}` : f.file.name
    })

    // Two staged files targeting the same path can't both land — the server
    // dedupes the preflight and the second would only fail with a per-file 409
    // mid-upload. Block up front; replace can't disambiguate same-batch dupes.
    const duplicates = findDuplicateTargetPaths(targetPaths)
    if (duplicates.length > 0) {
      toast.error('Duplicate file names in this selection', {
        description: `Rename or remove before uploading: ${duplicates.join(', ')}`,
      })
      return
    }

    // Preflight: surface same-name collisions before uploading anything so the
    // user replaces the whole batch or cancels it (SP-357). A failed check
    // falls back to a normal upload — the backend still guards with a 409.
    try {
      const { conflicts: found } = await api.cases.checkDocumentConflicts(caseId, targetPaths)
      if (found.length > 0) {
        setConflicts(found)
        return
      }
    } catch (error) {
      console.error('Conflict check failed:', error)
    }

    await runUpload(false)
  }, [files, caseId, buildFolderPath, runUpload])

  return (
    <>
      <CaseDocumentUploaderView
        caseName={caseName}
        open={open}
        onOpenChange={onOpenChange}
        basePath={basePath}
        files={files}
        uploading={uploading}
        onAddFiles={addFiles}
        onRemoveFile={removeFile}
        onClearFiles={clearFiles}
        onUploadAll={uploadAll}
        onSwitchToLake={onSwitchToLake}
        receivedOn={{ value: receivedOn, onChange: setReceivedOn }}
        tableProcessing={
          files.some(item => isTableFilename(item.file.name))
            ? { value: tableMode, onChange: setTableMode, onFileChange: changeFileTableMode }
            : undefined
        }
      />
      <UploadConflictDialog
        conflicts={conflicts}
        onReplace={() => {
          setConflicts([])
          void runUpload(true)
        }}
        onCancel={() => setConflicts([])}
      />
    </>
  )
}
